/**
 * Extract the bare email address from a string. Handles `"Name" <email>` as
 * well as plain `email` formats.
 *
 * - Angle-bracket input: extracts and lowercases the content between `<` and
 *   `>` without further validation (the envelope is trusted).
 * - Plain input: returns the trimmed, lowercased string only when it contains
 *   `@`; otherwise returns `''` (the input is likely a display name, not an
 *   address).
 * - Empty / whitespace-only input: returns `''`.
 */
export function extractEmail(raw: string): string {
  const match = raw.match(/<([^>]+)>/);
  if (match) {
    return match[1].trim().toLowerCase();
  }
  const trimmed = raw.trim().toLowerCase();
  return trimmed.includes('@') ? trimmed : '';
}

/**
 * Extract the display name from a `Name <email>` string, or `''` when there
 * isn't one.
 *
 * Returning `''` rather than falling back to the address is deliberate: callers
 * pass the result as `Participant.name`, and `ParticipantPillList` already owns
 * the no-name fallback (the email local-part). Substituting a fallback here
 * would override that in the one place it is meant to apply.
 *
 * - `"Jane Smith" <j@acme.com>` → `Jane Smith` — RFC-5322 quoting is stripped,
 *   including backslash escapes inside the quoted string. This is the case the
 *   three older copies of this parser get wrong (`textUtils.ts:extractSenderName`,
 *   `EmailThreadPanel.tsx:extractName`, and an inline copy in
 *   `useRecentImportItems.ts` all use `/^([^<]+)\s*</` and `.trim()`, which
 *   leaves the literal double quotes in the rendered name).
 * - `Jane Smith <j@acme.com>` → `Jane Smith`
 * - `j@acme.com`, `<j@acme.com>`, `''` → `''` (no name present)
 */
export function extractDisplayName(raw: string): string {
  const angle = raw.indexOf('<');
  if (angle === -1) {
    return '';
  }
  const name = raw.slice(0, angle).trim();
  if (name.length >= 2 && name.startsWith('"') && name.endsWith('"')) {
    return name.slice(1, -1).replace(/\\(.)/g, '$1').trim();
  }
  return name;
}
